import api from "../http/api";

export const listFiles = async (institutionId) => {
  try {
    const response = await api.client.get("api/v1/listFiles", {
      headers: {
        institutionId: institutionId
      }
    });
    return response.data;
  } catch (error) {
    throw new Error("Falha na conexão com a API de login");
  }
};

export const downloadFile = async (correlationId, institutionId) => {
  try {
    const response = await api.client.post(
      `api/v1/DownloadFile?correlationId=${correlationId}`,
      {},
      {
        headers: {
          institutionId: institutionId
        },
        responseType: "blob"
      }
    );

    return response.data;
  } catch (error) {
    console.error("Falha na conexão com a API de baixar arquivo");
    return null
  }
};

export const updateFileStatus = async (correlationId, institutionId) => {
  try {
    await api.client.post(
      `api/v1/UpdateFileStatus`,
      {
        fileId: correlationId
      },
      {
        headers: {
          institutionId: institutionId
        }
      }
    );
  } catch (error) {
    throw new Error("Falha na conexão com a API de inativar arquivo");
  }
};

export const updateFileVisibility = async (correlationId, institutionId) => {
  try {
    await api.client.post(
      `api/v1/UpdateFileVisibility`,
      {
        fileId: correlationId
      },
      {
        headers: {
          institutionId: institutionId
        }
      }
    );
  } catch (error) {
    throw new Error("Falha na conexão com a API de ocultar arquivo");
  }
};

export const validateFile = async ({ expectedHeaders, receivedHeaders, firstDataLine, institutionId, fileType }) => {
  try {
    const response = await api.client.post(
      `api/v1/FileValidation/validar-arquivo`,
      {
        expectedHeaders,
        receivedHeaders,
        firstDataLine,
        fileType
      },
      {
        headers: {
          institutionId: institutionId,
          "Content-Type": "application/json"
        }
      }
    );

    return response.data;
  } catch (error) {
    throw error?.response?.data || error?.response?.data?.message || 'Falha ao validar o arquivo. Por favor, verifique o formato.';
  }
};

export const uploadFile = async ({ file, institutionId, uploadType, ignoreSavedStudentStatusMappings, columnMapping }) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (ignoreSavedStudentStatusMappings) {
      formData.append("ignoreSavedStudentStatusMappings", "true");
    }
    if (columnMapping && typeof columnMapping === "object" && Object.keys(columnMapping).length > 0) {
      formData.append("columnMapping", JSON.stringify(columnMapping));
    }

    const response = await api.client.post(
      `api/v1/${uploadType}/import`,
      formData,
      {
        headers: {
          institutionId: institutionId,
          "Content-Type": "multipart/form-data"
        }
      }
    );

    // HTTP 202 Accepted = processamento em background (arquivo grande)
    if (response.status === 202) {
      return {
        isBackground: true,
        correlationId: response.data?.correlationId,
        message: response.data?.message || "O arquivo será processado em segundo plano."
      };
    }

    return response.data;
  } catch (error) {
    // Verifica se há dados de erro na resposta
    const errorData = error?.response?.data;
    
    // Se for um array de erros, retorna o array completo
    if (Array.isArray(errorData)) {
      throw errorData;
    }
    
    // Se for um objeto com erros e warnings
    if (errorData?.errors && Array.isArray(errorData.errors)) {
      const allErrors = [...errorData.errors];
      if (errorData.warnings && Array.isArray(errorData.warnings)) {
        allErrors.push('\n=== Avisos ===', ...errorData.warnings);
      }
      if (errorData.planLimit) {
        allErrors.planLimit = errorData.planLimit;
      }
      throw allErrors;
    }
    
    // Se for uma string simples
    if (typeof errorData === 'string') {
      throw errorData;
    }
    
    // Mensagem de erro genérica
    throw 'Falha ao enviar o arquivo. Por favor, tente novamente.';
  }
};

// Importação Inteligente (genérica/multi-tipo): detecta os tipos e mapeamentos do arquivo.
export const detectImportTypes = async ({ file, institutionId }) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.client.post(
      "api/v1/GenericImport/detect",
      formData,
      {
        headers: {
          institutionId: institutionId,
          "Content-Type": "multipart/form-data"
        }
      }
    );

    return response.data;
  } catch (error) {
    const errorData = error?.response?.data;
    if (errorData?.errors && Array.isArray(errorData.errors)) throw errorData.errors;
    if (typeof errorData === "string") throw errorData;
    throw "Falha ao analisar o arquivo. Por favor, tente novamente.";
  }
};

// Importação Inteligente: envia o arquivo com as detecções confirmadas na revisão.
// manualValidation = true mantém o gate de revisão manual (pausa quando a IA fica insegura);
// false (padrão) importa direto, sem devolver a validação ao usuário.
export const uploadGeneric = async ({ file, institutionId, detections, manualValidation }) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (detections && Array.isArray(detections) && detections.length > 0) {
      formData.append("detectionOverride", JSON.stringify(detections));
    }
    if (manualValidation) {
      formData.append("manualValidation", "true");
    }

    const response = await api.client.post(
      "api/v1/GenericImport/import",
      formData,
      {
        headers: {
          institutionId: institutionId,
          "Content-Type": "multipart/form-data"
        }
      }
    );

    if (response.status === 202) {
      return {
        isBackground: true,
        correlationId: response.data?.correlationId,
        message: response.data?.message || "O arquivo será processado em segundo plano."
      };
    }

    return response.data;
  } catch (error) {
    const errorData = error?.response?.data;
    if (errorData?.errors && Array.isArray(errorData.errors)) throw errorData.errors;
    if (Array.isArray(errorData)) throw errorData;
    if (typeof errorData === "string") throw errorData;
    throw "Falha ao enviar o arquivo. Por favor, tente novamente.";
  }
};

// Importação Inteligente: retoma uma importação pausada para mapeamento manual (sem re-upload),
// enviando o correlationId + as detecções ajustadas pelo usuário na tela de revisão.
export const resolveFieldMapping = async ({ correlationId, detections, headerSpec, institutionId }) => {
  try {
    const response = await api.client.post(
      "api/v1/GenericImport/resume-mapping",
      { correlationId, detections, headerSpec },
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    if (response.status === 202) {
      return {
        isBackground: true,
        correlationId: response.data?.correlationId,
        message: response.data?.message || "O arquivo será processado em segundo plano."
      };
    }

    return response.data;
  } catch (error) {
    const errorData = error?.response?.data;
    if (errorData?.errors && Array.isArray(errorData.errors)) throw errorData.errors;
    if (Array.isArray(errorData)) throw errorData;
    if (typeof errorData === "string") throw errorData;
    throw "Falha ao concluir o mapeamento. Por favor, tente novamente.";
  }
};

// Importação Inteligente: consulta o snapshot do mapeamento gravado ao final de uma importação
// concluída (canônico -> coluna do arquivo + amostra), para exibir em modo somente-leitura.
export const getImportMapping = async (correlationId, institutionId) => {
  try {
    const response = await api.client.get(
      `api/v1/GenericImport/mapping/${correlationId}`,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    const errorData = error?.response?.data;
    if (typeof errorData === "string") throw errorData;
    throw "Mapeamento não disponível para esta importação.";
  }
};

// Linhas brutas de um arquivo importado sem tipo identificado (guardado, mas sem gerar indicador).
export const getRawImportedData = async ({ fileId, institutionId, page = 1, pageSize = 50 }) => {
  try {
    const response = await api.client.get(
      'api/v1/GenericImport/raw-data',
      {
        params: { fileId, page, pageSize },
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    const errorData = error?.response?.data;
    if (typeof errorData === "string") throw errorData;
    throw "Não foi possível carregar os dados desse arquivo.";
  }
};

export const resolveStudentStatus = async ({ fileId, mappings, institutionId }) => {
  try {
    const response = await api.client.post(
      'api/v1/ResolveStudentStatus',
      { fileId, mappings },
      {
        headers: {
          institutionId: institutionId
        }
      }
    );
    return response.data;
  } catch (error) {
    const errorData = error?.response?.data;
    if (Array.isArray(errorData)) throw errorData;
    if (typeof errorData === 'string') throw new Error(errorData);
    throw new Error('Falha ao resolver status do aluno. Por favor, tente novamente.');
  }
};

export const exportCsvFromQuery = async ({ institutionId, sqlQuery, fileName }) => {
  try {
    const response = await api.client.post(
      'api/v1/CsvExport/export-from-query',
      {
        sqlQuery: sqlQuery,
        fileName: fileName
      },
      {
        headers: {
          institutionId: institutionId
        },
        responseType: 'blob'
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao exportar CSV:", error);
    throw new Error("Falha ao exportar CSV");
  }
};
